import React , { useState, useEffect, useRef } from "react";
import { compose } from "recompose";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import IntlTelInput from 'react-intl-tel-input';
import 'react-intl-tel-input/dist/main.css';
import withTranslator from "../../Translator/withTranslation";
import withFosRouter from "../../FosRouter/withFosRouting";
import FileSaver from 'file-saver';

const FormContainer = styled.div`
    box-shadow: 0 20px 20px 0px #333;
    min-height:300px;
    @media(max-width:768px) {
        min-height:200px;
        padding:1em;
        margin-bottom:2em;
    }
    @media(max-width:400px) {
        min-height:200px;
        margin-bottom:2em;
    }
`;

const LoadingContainer = styled.div`
    position:absolute;
    left:0;
    top:0;
    height:100%;
    width:100%;
    z-index:2;
    background: rgba(255,255,255,0.7);
    display:flex;
    justify-content: center;
    align-items: center;
`;

const rotate360 = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const Spinner = styled.div`
  animation: ${rotate360} 1s linear infinite;
  transform: translateZ(0);
  border-top: 2px solid #07ca;
  border-right: 2px solid #07ca;
  border-bottom: 2px solid #07cf;
  border-left: 2px solid #07c;
  background: transparent;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin: 0;
`;


const EBookDownloadFormComponent = (props) => {
    const [formStep, setFormStep] = useState(1);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [dataValid, setDataValid] = useState(false);
    const [firstNameError, setFirstNameError] = useState(false);
    const [lastNameError, setLastNameError] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [token, setToken] = useState("");
    const [loading, setLoading] = useState(false);
    const firstRender = useRef(true);
    const [downloaded, setDownloaded] = useState(false);
    const lastNameRef = useRef(true);
    const firstNameRef = useRef(true);
    const emailRef = useRef(true);
    const [emailHadFocus, setEmailHadFocus] = useState(false);

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false
            return;
        }
        setDataValid(checkFields());
    }, [firstName, lastName, email, emailHadFocus]);

    const checkFields = () => {
        if (typeof document !== 'undefined' && document.activeElement !== firstNameRef.current && !validateName(firstName)) {
            setFirstNameError(true);
            return false;
        } else {
            setFirstNameError(false);
        }
        if (typeof document !== 'undefined' && document.activeElement !== lastNameRef.current && !validateName(lastName)) {
            setLastNameError(true);
            return false;
        } else {
            setLastNameError(false);
        }
        if (typeof document !== 'undefined' && document.activeElement !== emailRef.current && emailHadFocus !== false && !validateEmail(email)) {
            setEmailError(true);
            return false;
        } else {
            setEmailError(false);
        }

        return true;
    }

    const sendForm = (nextStep) => {
        setLoading(true);
        setDataValid(checkFields());
        switch (nextStep) {
            case 1:
                setFormStep(nextStep);
            break;
            case 2:                
                if (!dataValid || emailHadFocus === false) {
                    setLoading(false);
                    setDataValid(checkFields());
                    return;
                }

                // make post request to some endpoint
                fetch(props.r('ebook_register'), {
                        method: "POST",
                        body: JSON.stringify({firstname: firstName, lastname: lastName, email: email, locale: props.locale}),
                        headers: {
                            "Content-Type": "application/json",
                            "X-Requested-With": "XMLHttpRequest"
                        }, 
                        credentials: 'same-origin', 
                    }
                ).then(response => response.json())
                .then(response => { 
                    if (response.status === 'ok' && response.token !== '' ) { 
                        setToken(response.token); 
                        setFormStep(nextStep)
                        setLoading(false);
                    } else {
                        setDataValid(checkFields());
                        setLoading(false);
                    }
                 });
            break;
        }
    }

    const validateEmail = (email) => {
        if (/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email)) {
            return true;
        }
        return false;
    }
    const validateName = (name) => {
        if (/^(?:((([^0-9_!¡?÷?¿/\\+=@#$%ˆ&*(){}|~<>;:[\]'’,\-.\s])){1,}(['’,\-\.]){0,1}){2,}(([^0-9_!¡?÷?¿/\\+=@#$%ˆ&*(){}|~<>;:[\]'’,\-. ]))*(([ ]+){0,1}(((([^0-9_!¡?÷?¿/\\+=@#$%ˆ&*(){}|~<>;:[\]'’,\-\.\s])){1,})(['’\-,\.]){0,1}){2,}((([^0-9_!¡?÷?¿/\\+=@#$%ˆ&*(){}|~<>;:[\]'’,\-\.\s])){2,})?)*)$/.test(name)) {
            return true;
        }
        return false;
    }
    // ...
    const onDownload = (token) => {
        const { locale , r} = props;
        const filename = "club-villamar-ebook-" + locale + ".pdf"
        setLoading(true);
        fetch(r('ebook_download', {token: token, filename: filename}), {
            responseType: 'blob'
        })
        .then(response => response.blob())
        .then(blob => { 
            FileSaver.saveAs(blob , filename);
            setDownloaded(true);
            setLoading(false);
     });
    }   

    const locale = props.locale == 'ca_es' ? 'es' : props.locale;
    switch (formStep) {
        case 1:
            return (
                <FormContainer className="bg-white border-radius-default m-lg-3 m-md-1 p-lg-3 p-1 align-sevenly relative">
                    {loading && <LoadingContainer className="border-radius-default" ><Spinner /></LoadingContainer>}
                    <div className="m-3">
                        <div className="py-2 my-3">
                            <input ref={firstNameRef} className="width-100" type="text" defaultValue={firstName} onBlur={(e) => {setFirstName(e.target.value)}} placeholder={props.t('firstname')} />
                            {firstNameError && <div className="red font-80 italic absolute">{props.t('provide_first_name')}</div>}
                        </div>
                        <div className="py-2 my-3">
                            <input ref={lastNameRef} className="width-100" type="text" defaultValue={lastName} onBlur={(e) => {setLastName(e.target.value)}} placeholder={props.t('surname')} />
                            {lastNameError && <div className="red font-80 italic absolute">{props.t('provide_last_name')}</div>}
                        </div>
                        <div className="py-2 my-3">
                            <input ref={emailRef} className="width-100 " type="email" defaultValue={email} onFocus={(e)=> {setEmailHadFocus(true)}} onBlur={(e) => { setEmail(e.target.value) }} placeholder={props.t('email')} />
                            {emailError && <div className="red font-80 italic absolute">{props.t('provide_valid_email')} </div>}
                        </div>
                        <div className="py-2 my-3"><div className="text-center btn btn-primary font-w600 pt-3 font-130" tabIndex="0" onClick={(value) => {sendForm(2)}}  dangerouslySetInnerHTML={{ __html: props.t('get_my_free_lloret_de_mar_ebook') }}></div></div>
                    </div>
                </FormContainer>
            );
        case 2:
            return (
                <FormContainer className="bg-white border-radius-default m-3 p-3 align-sevenly">
                    {loading && <LoadingContainer  className="border-radius-default" ><Spinner /></LoadingContainer>}
                    <div className="py-2 my-3  py-lg-5 height-100 flex flex-column justify-center"> 
                        <div className="font-w600 font-150 text-center pb-3">{props.t('thank_you')}</div>
                        {!downloaded && <div className="text-center btn btn-primary font-w600 pt-3 font-130" onClick={() => {onDownload(token)}} dangerouslySetInnerHTML={{__html: props.t('download_ebook')}}></div>}
                    </div>
                </FormContainer>
            )
    }

    
}

export const EBookDownloadForm = compose(
    withTranslator,
    withFosRouter
)(EBookDownloadFormComponent);